import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const InteractiveSurvey = () => {
  const { t } = useLanguage();
  const [step, setStep] = useState(1); // 1: Rating, 2: Topics, 3: Comment, 4: Success
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const topics = [
    { id: 'speakers', label: '📢 Speakers' },
    { id: 'venue', label: '🏢 Venue' },
    { id: 'networking', label: '🤝 Networking' },
    { id: 'organization', label: '📋 Organization' },
    { id: 'content', label: '💡 Content Quality' },
  ];

  const ratingLabels = {
    1: 'Poor 😞',
    2: 'Fair 😐',
    3: 'Average 🙂',
    4: 'Good 😊',
    5: 'Excellent! 🤩',
  };

  const handleRating = (value) => {
    setRating(value);
    setTimeout(() => {
      setStep(2);
    }, 450);
  };

  const toggleTopic = (id) => {
    if (selectedTopics.includes(id)) {
      setSelectedTopics(selectedTopics.filter(t => t !== id));
    } else {
      setSelectedTopics([...selectedTopics, id]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setStep(4);
    }, 1500);
  };

  const resetSurvey = () => {
    setStep(1);
    setRating(0);
    setHoveredRating(0);
    setSelectedTopics([]);
    setComment("");
  };

  return (
    <div className="w-full bg-white rounded-2xl border border-slate-100 p-6 shadow-sm min-h-[360px] flex flex-col justify-center">
      <AnimatePresence mode="wait">
        
        {/* Step 1: Star Rating */}
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="text-center py-4"
          >
            <h3 className="text-lg font-extrabold text-slate-900 mb-2">Rate Your Experience</h3>
            <p className="text-xs text-slate-500 mb-8 max-w-sm mx-auto">
              How would you describe the overall quality of BookMeIn.Events?
            </p>
            
            <div className="flex justify-center items-center gap-3 mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="text-slate-200 hover:text-amber-400 focus:outline-none transition-all transform hover:scale-125 duration-150"
                >
                  <SafeIcon
                    name="Star"
                    className={`w-10 h-10 shrink-0 ${
                      (hoveredRating || rating) >= star
                        ? 'text-amber-400 fill-amber-400'
                        : 'text-slate-200'
                    } transition-all duration-150`}
                  />
                </button>
              ))}
            </div>
            
            <div className="h-6 text-sm font-bold text-indigo-600 transition-all">
              {ratingLabels[hoveredRating || rating] || 'Select your rating'}
            </div>
          </motion.div>
        )}

        {/* Step 2: Topic Highlights */}
        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col h-full"
          >
            <div className="flex items-center justify-between mb-6">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 font-bold"
              >
                <SafeIcon name="ArrowLeft" className="w-3.5 h-3.5" /> Back
              </button>
              <span className="text-[10px] bg-indigo-50 text-indigo-600 font-extrabold px-2 py-0.5 rounded-full">Step 2 of 3</span>
            </div>
            
            <h3 className="text-base font-extrabold text-slate-900 mb-2">What stood out the most?</h3>
            <p className="text-xs text-slate-500 mb-6">Choose highlights that contributed to your experience.</p>
            
            <div className="flex flex-wrap gap-2.5 mb-8">
              {topics.map((topic) => {
                const isSelected = selectedTopics.includes(topic.id);
                return (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => toggleTopic(topic.id)}
                    className={`px-4 py-2.5 rounded-full text-xs font-bold transition-all duration-200 flex items-center gap-2 border ${
                      isSelected
                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-500/20 scale-105'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {topic.label}
                  </button>
                );
              })}
            </div>
            
            <button
              type="button"
              disabled={selectedTopics.length === 0}
              onClick={() => setStep(3)}
              className={`w-full py-3.5 rounded-xl text-xs font-bold text-white transition-all shadow-md flex items-center justify-center gap-2 ${
                selectedTopics.length > 0
                  ? 'bg-gradient-primary hover:shadow-indigo-500/20 hover:shadow-lg active:scale-98'
                  : 'bg-slate-300 cursor-not-allowed shadow-none'
              }`}
            >
              Continue <SafeIcon name="ArrowRight" className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {/* Step 3: Written Suggestions */}
        {step === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <form onSubmit={handleSubmit}>
              <div className="flex items-center justify-between mb-6">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 font-bold"
                >
                  <SafeIcon name="ArrowLeft" className="w-3.5 h-3.5" /> Back
                </button>
                <span className="text-[10px] bg-indigo-50 text-indigo-600 font-extrabold px-2 py-0.5 rounded-full">Step 3 of 3</span>
              </div>
              
              <h3 className="text-base font-extrabold text-slate-900 mb-2">Any additional feedback?</h3>
              <p className="text-xs text-slate-500 mb-4">Your answers help event organizers build better experiences.</p>
              
              <div className="relative mb-6">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value.slice(0, 200))}
                  rows="4"
                  placeholder="Type your suggestion here..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white focus:border-transparent transition-all resize-none leading-relaxed"
                />
                <div className="absolute right-3 bottom-3 text-[10px] text-slate-400 font-bold">
                  {comment.length}/200
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-primary py-3.5 rounded-xl text-xs font-bold text-white shadow-md hover:shadow-indigo-500/25 hover:shadow-lg active:scale-98 transition-all flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4.5 h-4.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Feedback <SafeIcon name="Check" className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        )}

        {/* Step 4: Success Stats Screen */}
        {step === 4 && (
          <motion.div
            key="step4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="text-center py-2"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
              className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 mx-auto mb-4 border border-emerald-200"
            >
              <SafeIcon name="Check" className="w-6 h-6" />
            </motion.div>
            
            <h3 className="text-lg font-extrabold text-slate-900 mb-1">Feedback Submitted!</h3>
            <p className="text-xs text-slate-500 mb-6">Your answers have been analyzed and visualized below.</p>
            
            {/* Live Analytics Graph */}
            <div className="bg-slate-50 border border-slate-200/50 p-4 rounded-xl mb-6 text-left">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-3.5 flex items-center justify-between">
                <span>Real-Time Survey Analytics</span>
                <span className="text-indigo-600 font-black">124 responses</span>
              </div>
              
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-[10px] font-bold text-slate-700 mb-1">
                    <span>Excellent (5 stars)</span>
                    <span>78%</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: '78%' }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      className="h-full bg-indigo-600 rounded-full"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-[10px] font-bold text-slate-700 mb-1">
                    <span>Good (4 stars)</span>
                    <span>16%</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: '16%' }}
                      transition={{ duration: 1, ease: 'easeOut', delay: 0.1 }}
                      className="h-full bg-indigo-400 rounded-full"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-[10px] font-bold text-slate-700 mb-1">
                    <span>Average (3 stars)</span>
                    <span>6%</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: '6%' }}
                      transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
                      className="h-full bg-indigo-300 rounded-full"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <button
              type="button"
              onClick={resetSurvey}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-700 hover:underline flex items-center gap-1.5 mx-auto"
            >
              <SafeIcon name="RotateCcw" className="w-3.5 h-3.5" /> Restart Demo Survey
            </button>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
};

const FeedbackSection = () => {
  const { t } = useLanguage();

  const featureHighlights = [
    {
      icon: "Zap",
      title: t("feedback.highlights.deliveryTitle"),
      desc: t("feedback.highlights.deliveryDesc")
    },
    {
      icon: "PieChart",
      title: t("feedback.highlights.analysisTitle"),
      desc: t("feedback.highlights.analysisDesc")
    },
    {
      icon: "Star",
      title: t("feedback.highlights.reviewsTitle"),
      desc: t("feedback.highlights.reviewsDesc")
    }
  ];

  return (
    <section id="feedback" className="py-24 bg-white overflow-hidden font-sans">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center w-full">
          
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold uppercase tracking-wider mb-6">
              <SafeIcon name="MessageSquare" className="w-4 h-4" />
              {t('feedback.badge')}
            </div>
            
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight">
              {t('feedback.title')} <span className="text-gradient">{t('feedback.titleSpan')}</span>
            </h2>
            
            <p className="text-lg text-slate-600 mb-10 leading-relaxed">
              {t('feedback.desc')}
            </p>

            <div className="space-y-8">
              {featureHighlights.map((item, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                    <SafeIcon name={item.icon} className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-900 mb-1">{item.title}</h4>
                    <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative w-full"
          >
            {/* Decorative background blur */}
            <div className="absolute -inset-4 bg-gradient-to-tr from-indigo-500/10 to-purple-500/10 rounded-[40px] blur-2xl -z-10 animate-pulse"></div>
            
            {/* Professional Mockup Container */}
            <div className="bg-slate-50 p-4 sm:p-8 rounded-[32px] border border-slate-200 shadow-2xl">
              <div className="mb-6 flex items-center justify-between">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-rose-400"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                  <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                </div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('feedback.preview')}</div>
              </div>
              
              {/* Integration of the Rich Interactive Survey */}
              <div className="max-w-md mx-auto">
                <InteractiveSurvey />
              </div>
            </div>

            {/* Floating Badge */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
              className="absolute -right-8 top-1/2 bg-white p-4 rounded-2xl shadow-xl border border-indigo-100 hidden md:flex items-center gap-3 z-10"
            >
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                <SafeIcon name="CheckCircle" className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-900">{t('feedback.rating')}</div>
                <div className="text-[10px] text-slate-500">{t('feedback.ratingSub')}</div>
              </div>
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default FeedbackSection;